// Stable-id reconciliation for animated chess boards. Pure logic over 8x8
// grids of FEN piece chars (string|null) — no DOM, no platform deps — so the
// web and React Native boards share one algorithm.
//
// On each position change we diff the previous placement (pieces with stable
// ids) against the new board and carry ids across so a moved piece keeps its
// identity (and thus animates). Matching is by piece type: unchanged squares
// first, then nearest surviving same-type square (the one that moved); leftover
// pieces (newly appeared / promoted to a different type) get fresh ids.

export type BoardGrid = (string | null)[][];

// A piece placed on the board, with a stable id carried across position changes.
export interface PlacedPiece {
  id: number;
  piece: string;
  row: number;
  col: number;
}

/**
 * Diff `prev` placements against `board` and return the new placement, carrying
 * stable ids across so moved pieces keep their id. `nextId` mints fresh ids for
 * pieces with no match (new arrivals, promotions to a new type).
 *
 * Note: promotion keeps the pawn's id with the *new* piece type only when the
 * promoted type already had an unused predecessor to match (e.g. a queen). When
 * a pawn promotes to a brand-new type with no predecessor it gets a fresh id —
 * this mirrors the original web behavior (match is keyed by piece char).
 */
export function reconcilePieces(
  prev: PlacedPiece[],
  board: BoardGrid,
  nextId: () => number,
): PlacedPiece[] {
  const current: { piece: string; row: number; col: number }[] = [];
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const piece = board[r]?.[c] ?? null;
      if (piece) current.push({ piece, row: r, col: c });
    }
  }
  const prevByType = new Map<string, PlacedPiece[]>();
  for (const p of prev) {
    const arr = prevByType.get(p.piece) ?? [];
    arr.push(p);
    prevByType.set(p.piece, arr);
  }
  const result: PlacedPiece[] = [];
  const used = new Set<number>();
  // Pass 1: exact same square+type keeps its id (most pieces don't move).
  const pending: typeof current = [];
  for (const cur of current) {
    const pool = prevByType.get(cur.piece) ?? [];
    const exact = pool.find((p) => !used.has(p.id) && p.row === cur.row && p.col === cur.col);
    if (exact) {
      used.add(exact.id);
      result.push({ id: exact.id, ...cur });
    } else {
      pending.push(cur);
    }
  }
  // Pass 2: remaining pieces match the nearest unused same-type piece (the one
  // that moved); leftover (newly appeared / promoted) get fresh ids.
  for (const cur of pending) {
    const pool = (prevByType.get(cur.piece) ?? []).filter((p) => !used.has(p.id));
    let best: PlacedPiece | null = null;
    let bestD = Infinity;
    for (const p of pool) {
      const d = Math.abs(p.row - cur.row) + Math.abs(p.col - cur.col);
      if (d < bestD) {
        bestD = d;
        best = p;
      }
    }
    if (best) {
      used.add(best.id);
      result.push({ id: best.id, ...cur });
    } else {
      result.push({ id: nextId(), ...cur });
    }
  }
  return result;
}
